
import React, { useState } from 'react';
import { MessageSquare, Send, Sparkles, X, CheckCircle, Loader2, HelpCircle, Bug, Lightbulb } from 'lucide-react';
import { useAuth } from './AuthBridge';
import { StorageService } from '../services/storageService';

export const FeedbackWidget: React.FC = () => {
    const { user, activeCert } = useAuth();
    const [isOpen, setIsOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [formData, setFormData] = useState({
        category: 'General Feedback',
        message: ''
    });

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.message.trim() || isSubmitting) return;

        setIsSubmitting(true);
        try {
            const res = await fetch('/practice-exams/api/feedback', {
                method: 'POST',
                headers: StorageService.getAuthHeader(),
                body: JSON.stringify({
                    ...formData,
                    state: user?.state,
                    certId: activeCert?.id,
                    metadata: {
                        userAgent: navigator.userAgent,
                        platform: navigator.platform
                    }
                })
            });

            if (res.ok) {
                setIsSuccess(true);
                setFormData({ category: 'General Feedback', message: '' });
                setTimeout(() => {
                    setIsSuccess(false);
                    setIsOpen(false);
                }, 3000);
            }
        } catch (e) {
            console.error("Feedback submission failure", e);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) {
        return (
            <button 
                onClick={() => setIsOpen(true)}
                className="bg-white p-6 rounded-[2.5rem] border border-slate-200 shadow-sm hover:shadow-xl hover:border-indigo-300 transition-all group w-full text-left flex items-center justify-between"
            >
                <div className="flex items-center gap-4">
                    <div className="p-3 bg-indigo-50 text-indigo-600 rounded-2xl group-hover:scale-110 transition-transform">
                        <HelpCircle size={20} />
                    </div>
                    <div>
                        <h4 className="font-black text-sm tracking-tight text-slate-800">Help & Feedback</h4>
                        <p className="text-[10px] font-bold text-slate-400 tracking-widest mt-1">Report bugs or suggest features</p>
                    </div>
                </div>
                <ChevronRight size={18} className="text-slate-200 group-hover:text-indigo-400 transition-colors" />
            </button>
        );
    }

    return (
        <div className="bg-white rounded-[2.5rem] border-2 border-indigo-500 p-8 shadow-2xl animate-pop space-y-6 relative overflow-hidden">
            <div className="absolute top-0 right-0 p-4 opacity-5 pointer-events-none"><MessageSquare size={100}/></div>
            
            <div className="flex justify-between items-center relative z-10">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-indigo-600 text-white rounded-xl shadow-lg"><Sparkles size={16}/></div>
                    <h3 className="text-lg font-black tracking-tight">Feedback & Support</h3>
                </div>
                <button onClick={() => setIsOpen(false)} className="p-2 text-slate-300 hover:text-rose-500 transition-all"><X size={20}/></button>
            </div>

            {isSuccess ? (
                <div className="py-12 flex flex-col items-center justify-center text-center space-y-4 animate-fade-in">
                    <div className="w-16 h-16 bg-emerald-50 text-emerald-500 rounded-full flex items-center justify-center shadow-inner border border-emerald-100">
                        <CheckCircle size={32} />
                    </div>
                    <div className="space-y-1">
                        <p className="font-black text-slate-900">Message Logged</p>
                        <p className="text-xs text-slate-400 font-bold">"The support registry has received your input."</p>
                    </div>
                </div>
            ) : (
                <form onSubmit={handleSubmit} className="space-y-6 relative z-10">
                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">What can we help with?</label>
                        <div className="grid grid-cols-1 gap-2">
                            <select 
                                value={formData.category}
                                onChange={e => setFormData({...formData, category: e.target.value as any})}
                                className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-xs outline-none focus:bg-white transition-all shadow-inner"
                            >
                                <option value="Bug Report">🪲 Report a technical bug</option>
                                <option value="Content Correction">📚 Report a content error</option>
                                <option value="Feature Request">💡 Suggest a new feature</option>
                                <option value="General Feedback">💬 General comment</option>
                            </select>
                        </div>
                    </div>
                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-400 tracking-widest ml-1">Your Message</label>
                        <textarea 
                            value={formData.message}
                            onChange={e => setFormData({...formData, message: e.target.value})}
                            required
                            placeholder="Describe the issue, correction, or feature idea..."
                            className="w-full p-6 bg-slate-50 border border-slate-200 rounded-3xl font-medium text-sm outline-none focus:bg-white h-32 resize-none shadow-inner leading-relaxed"
                        />
                    </div>
                    <button 
                        type="submit"
                        disabled={isSubmitting || !formData.message.trim()}
                        className="w-full py-4 bg-slate-900 text-white rounded-2xl font-black text-[10px] tracking-[0.2em] shadow-xl hover:bg-black transition-all flex items-center justify-center gap-3 disabled:opacity-50 border-b-4 border-black"
                    >
                        {isSubmitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                        Submit to Registry
                    </button>
                    <p className="text-[8px] font-bold text-slate-400 text-center tracking-widest opacity-60">"Your device info and active state board are logged automatically."</p>
                </form>
            )}
        </div>
    );
};

// Internal component for cleaner code
const ChevronRight = ({size, className}: {size: number, className?: string}) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="m9 18 6-6-6-6"/></svg>
);
